// The proposal: which sections it has, in what order, and its custom text
// sections, kept in the quote's `proposal` document beside the terms.

export type SectionKey =
  | 'contents'
  | 'summary'
  | 'included'
  | 'timeline'
  | 'options'
  | 'investment'
  | 'payment'
  | 'roi'
  | 'beyond'
  | 'assumptions'
  | 'foundation'
  | 'signatures'
  | 'ask'
  | 'problem'
  | 'solution'
  | 'risks'
  | 'measures'
  | 'finance'
  | 'alternatives'
  | 'roles'
  | 'exit'
  | 'lines'
  | 'it';

export interface SectionRef {
  /** A built-in section, or `custom:<id>`. */
  key: SectionKey | `custom:${string}`;
  enabled: boolean;
  /** In the appendix, lettered A, B, C after the main sections. */
  appendix?: boolean;
  /** The section's title in this proposal; blank for its usual one. */
  title?: string;
}

export interface CustomSection {
  id: string;
  title: string;
  /** Blank line between paragraphs; a leading dash makes a bullet. */
  body: string;
}

export const SECTION_TITLES: Record<SectionKey, string> = {
  contents: 'Contents',
  summary: 'Summary',
  included: "What's included",
  timeline: 'Timeline',
  options: 'Options',
  investment: 'Investment',
  payment: 'Payment terms',
  roi: 'Return on investment',
  beyond: 'Beyond the numbers',
  assumptions: 'Assumptions',
  foundation: 'Olytic Foundation',
  signatures: 'Signatures',
  ask: 'The ask',
  problem: 'The problem today',
  solution: "What we'll build",
  risks: "Risks and how we'll handle them",
  measures: "How we'll measure success",
  finance: 'Financial detail',
  alternatives: 'Alternatives considered',
  roles: 'Roles and commitments',
  exit: 'Data ownership and exit',
  lines: 'Line items',
  it: 'For IT: security and support',
};

const ALL_KEYS = Object.keys(SECTION_TITLES) as SectionKey[];

/** The two starting layouts: which sections lead, and which go in the appendix. */
export const LAYOUTS: Record<'proposal' | 'memo', { label: string; main: SectionKey[]; appendix: SectionKey[] }> = {
  proposal: {
    label: 'Proposal',
    main: ['contents', 'summary', 'included', 'timeline', 'options', 'investment', 'roi', 'beyond', 'signatures'],
    appendix: ['payment', 'assumptions', 'foundation'],
  },
  memo: {
    label: 'Investment memo',
    main: ['contents', 'summary', 'problem', 'solution', 'roi', 'beyond', 'investment'],
    appendix: ['finance', 'assumptions', 'risks', 'alternatives', 'it', 'lines', 'timeline', 'payment', 'foundation'],
  },
};
export type LayoutName = keyof typeof LAYOUTS;

/**
 * The sections in a layout's order: its main sections, the custom sections
 * where they were, signatures last of the main ones, then its appendix. Every
 * other built-in section follows, turned off.
 */
export function applyLayout(sections: SectionRef[], name: LayoutName): SectionRef[] {
  const layout = LAYOUTS[name];
  const out: SectionRef[] = layout.main.map((key) => ({ key, enabled: true, appendix: false }));
  for (const s of sections) if (s.key.startsWith('custom:')) out.push({ ...s });
  const sig = out.findIndex((s) => s.key === 'signatures');
  if (sig > -1) out.push(...out.splice(sig, 1));
  for (const key of layout.appendix) out.push({ key, enabled: true, appendix: true });
  for (const key of ALL_KEYS) if (!out.some((s) => s.key === key)) out.push({ key, enabled: false, appendix: false });
  return out;
}

/** What the investment table shows: the proposal builder's toggles. */
export interface InvestmentSettings {
  groups: boolean;
  lines: boolean;
  subtotals: boolean;
  quantity: boolean;
  listPrice: boolean;
  price: boolean;
  discount: boolean;
  totalDiscount: boolean;
  /** Recurring prices per year rather than per month. */
  annual: boolean;
  /** Payment terms inside the Investment section, rather than a section of their own. */
  withPayment: boolean;
  /** Investment as the invoice schedule alone (date, for, amount) and its total: for a retainer. */
  scheduleOnly: boolean;
}

export const DEFAULT_INVESTMENT: InvestmentSettings = {
  groups: true,
  lines: true,
  subtotals: true,
  quantity: true,
  listPrice: false,
  price: true,
  discount: false,
  totalDiscount: true,
  annual: false,
  withPayment: false,
  scheduleOnly: false,
};

/** The column headings of the What's included table. */
export interface IncludedColumns {
  theme: string;
  covers: string;
}
export const DEFAULT_INCLUDED_COLUMNS: IncludedColumns = { theme: 'Requirement', covers: 'What it covers' };

export interface ProposalLayout {
  sections: SectionRef[];
  custom: CustomSection[];
  investment: InvestmentSettings;
  includedColumns: IncludedColumns;
}

/**
 * The stored layout. With none stored, the Proposal layout. A built-in section
 * it lacks is added where the Proposal layout has it (on, or in the appendix),
 * or turned off; unknown keys are dropped.
 */
export function readLayout(proposal: unknown): ProposalLayout {
  const stored = proposal && typeof proposal === 'object' ? (proposal as Partial<ProposalLayout>) : {};
  const custom = Array.isArray(stored.custom) ? stored.custom : [];
  const investment = { ...DEFAULT_INVESTMENT, ...(stored.investment && typeof stored.investment === 'object' ? stored.investment : {}) };
  const includedColumns = { ...DEFAULT_INCLUDED_COLUMNS, ...(stored.includedColumns && typeof stored.includedColumns === 'object' ? stored.includedColumns : {}) };
  const known = new Set<string>([...ALL_KEYS, ...custom.map((c) => `custom:${c.id}`)]);
  const storedSections = (Array.isArray(stored.sections) ? stored.sections : []).filter((s) => known.has(s.key));
  if (!storedSections.length) {
    const sections = applyLayout(custom.map((c) => ({ key: `custom:${c.id}` as const, enabled: true })), 'proposal');
    return { sections, custom, investment, includedColumns };
  }
  const sections = [...storedSections];
  const { main, appendix } = LAYOUTS.proposal;
  for (const key of ALL_KEYS) {
    if (sections.some((x) => x.key === key)) continue;
    const ref: SectionRef = { key, enabled: main.includes(key) || appendix.includes(key), appendix: appendix.includes(key) };
    if (key === 'contents') sections.unshift(ref);
    else sections.push(ref);
  }
  for (const c of custom) if (!sections.some((x) => x.key === `custom:${c.id}`)) sections.push({ key: `custom:${c.id}`, enabled: true });
  return { sections, custom, investment, includedColumns };
}

export function titleOf(ref: SectionRef, layout: Pick<ProposalLayout, 'custom'>): string {
  if (ref.key.startsWith('custom:')) return layout.custom.find((c) => `custom:${c.id}` === ref.key)?.title || 'Untitled section';
  return ref.title?.trim() || SECTION_TITLES[ref.key as SectionKey];
}

/** Contents and signatures always stay in the main part. */
export const inAppendix = (ref: SectionRef) => Boolean(ref.appendix) && ref.key !== 'contents' && ref.key !== 'signatures';

/** Moves the section at `index` up (-1) or down (+1). */
export function move(sections: SectionRef[], index: number, by: -1 | 1): SectionRef[] {
  const to = index + by;
  if (to < 0 || to >= sections.length) return sections;
  const next = [...sections];
  [next[index], next[to]] = [next[to]!, next[index]!];
  return next;
}

export type Block = { kind: 'paragraph'; text: string } | { kind: 'bullets'; items: string[] };

/** Custom section text to blocks: a blank line ends a paragraph, lines starting with "- " are bullets. */
export function parseBody(body: string): Block[] {
  const blocks: Block[] = [];
  for (const chunk of body.split(/\n\s*\n/)) {
    const lines = chunk.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!lines.length) continue;
    let paragraph: string[] = [];
    let bullets: string[] = [];
    const flush = () => {
      if (paragraph.length) blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
      if (bullets.length) blocks.push({ kind: 'bullets', items: bullets });
      paragraph = [];
      bullets = [];
    };
    for (const line of lines) {
      if (/^[-–•]\s+/.test(line)) {
        if (paragraph.length) flush();
        bullets.push(line.replace(/^[-–•]\s+/, ''));
      } else {
        if (bullets.length) flush();
        paragraph.push(line);
      }
    }
    flush();
  }
  return blocks;
}
